import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft,
  ArrowRight,
  Building2,
  Users,
  BookOpen,
  Plus,
  Search,
  MapPin,
  Check,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  SortAsc
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorDashboard.css';

/**
 * CuratorCreateCollectionWizard
 * 
 * A 3-step wizard for creating a collection:
 * 1. Select Organization (who is running the program)
 * 2. Select or Create Gallery (where it's happening)
 * 3. Redirect to collection creation form
 */
const CuratorCreateCollectionWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Pre-selected values from URL
  const preselectedOrgId = searchParams.get('org');
  const preselectedGalleryId = searchParams.get('gallery');
  
  // Wizard state
  const [step, setStep] = useState(preselectedOrgId ? 2 : 1);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedGallery, setSelectedGallery] = useState(null);
  
  // Data state
  const [organizations, setOrganizations] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleriesLoading, setGalleriesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Sort and pagination state
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'type' | 'address'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  
  // Helper to add Cloudinary transformation
  const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', `/upload/${transform}`);
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // If org is preselected, load galleries immediately
  useEffect(() => {
    if (preselectedOrgId && organizations.length > 0) {
      const org = organizations.find(m => 
        m.organization_id === parseInt(preselectedOrgId) || 
        m.organizations?.organization_id === parseInt(preselectedOrgId)
      );
      if (org) {
        setSelectedOrg(org.organizations || org);
        // Don't auto-load galleries - wait for user to search
      }
    }
  }, [preselectedOrgId, organizations]);

  // If gallery is also preselected (coming back from gallery creation), load and select it
  useEffect(() => {
    if (preselectedGalleryId && selectedOrg) {
      loadPreselectedGallery();
    }
  }, [preselectedGalleryId, selectedOrg]);

  const loadPreselectedGallery = async () => {
    try {
      const { data, error } = await supabase
        .from('galleries')
        .select('gallery_id, name, address, type, logo, cover_image, approved, organization_id')
        .eq('gallery_id', parseInt(preselectedGalleryId))
        .single();
      
      if (!error && data) {
        setSelectedGallery(data);
        // Show this gallery in the list
        setGalleries([data]);
        setTotalCount(1);
        setHasSearched(true);
      }
    } catch (err) {
      console.error('Error loading preselected gallery:', err);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch organization memberships
      const { data: orgs, error } = await supabase
        .from('organization_members')
        .select(`
          *,
          organizations:organization_id (
            organization_id,
            name,
            slug,
            description,
            logo
          )
        `)
        .eq('user_uuid', user.id)
        .eq('is_active', true);

      if (!error) {
        setOrganizations(orgs || []);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGalleries = async (search = '', page = 1) => {
    // Only search if there's a query
    if (!search.trim()) {
      setGalleries([]);
      setTotalCount(0);
      setHasSearched(false);
      return;
    }
    
    try {
      setGalleriesLoading(true);
      setHasSearched(true);
      
      // First get total count
      const { count } = await supabase
        .from('galleries')
        .select('gallery_id', { count: 'exact', head: true })
        .eq('approved', true)
        .or(`name.ilike.%${search}%,address.ilike.%${search}%`);
      
      setTotalCount(count || 0);
      
      // Then get paginated results
      const offset = (page - 1) * pageSize;
      
      let query = supabase
        .from('galleries')
        .select('gallery_id, name, address, type, logo, cover_image, approved, organization_id')
        .eq('approved', true)
        .or(`name.ilike.%${search}%,address.ilike.%${search}%`)
        .order(sortBy, { ascending: true })
        .range(offset, offset + pageSize - 1);

      const { data, error } = await query;

      if (!error) {
        setGalleries(data || []);
      }

    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setGalleriesLoading(false);
    }
  };

  const handleOrgSelect = (membership) => {
    setSelectedOrg(membership.organizations);
    setStep(2);
    // Don't load galleries until user searches
  };

  const handleGallerySelect = (gallery) => {
    setSelectedGallery(gallery);
    // Clear search and hide list after selection
    setSearchQuery('');
    setGalleries([]);
    setHasSearched(false);
  };
  
  const handleClearSelection = () => {
    setSelectedGallery(null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on new search
    
    // Debounce search
    clearTimeout(window.gallerySearchTimeout);
    window.gallerySearchTimeout = setTimeout(() => {
      loadGalleries(value, 1);
    }, 300);
  };
  
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
    if (searchQuery.trim()) {
      loadGalleries(searchQuery, 1);
    }
  };
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadGalleries(searchQuery, newPage);
  };
  
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleContinue = () => {
    if (step === 2 && selectedGallery) {
      // Navigate to the collection creation form with org and gallery pre-selected
      navigate(`/curator/galleries/${selectedGallery.gallery_id}/create-collection?org=${selectedOrg.organization_id}`);
    }
  };

  const handleCreateNewGallery = () => {
    // Navigate to gallery creation with org pre-selected
    navigate(`/curator/galleries/new?org=${selectedOrg.organization_id}&returnTo=collection`);
  };

  if (loading) {
    return (
      <div className="curator-overview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="curator-overview">
        <Link to="/curator/collections" className="back-link">
          <ArrowLeft size={16} />
          Back to Collections
        </Link>
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <Users size={48} color="#6c757d" />
          <h2>No Organization Access</h2>
          <p>You need to be a member of an organization to create collections. Please contact an administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-overview org-detail-page">
      {/* Header */}
      <div className="org-detail-header">
        <Link to="/curator/collections" className="back-link">
          <ArrowLeft size={16} />
          Back to Collections
        </Link>
        
        <h1 className="org-title" style={{ marginTop: '1rem' }}>Create New Exhibition or Collection</h1>
        
        {/* Step indicator */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          marginTop: '1rem',
          alignItems: 'center'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            color: step >= 1 ? '#007bff' : '#6c757d'
          }}>
            <div style={{ 
              width: '28px', 
              height: '28px', 
              borderRadius: '50%', 
              background: step >= 1 ? '#007bff' : '#e9ecef',
              color: step >= 1 ? 'white' : '#6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              {step > 1 ? <Check size={16} /> : '1'}
            </div>
            <span style={{ fontWeight: step === 1 ? 600 : 400 }}>Organization</span>
          </div>
          
          <div style={{ width: '40px', height: '2px', background: step >= 2 ? '#007bff' : '#e9ecef' }} />
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            color: step >= 2 ? '#007bff' : '#6c757d'
          }}>
            <div style={{ 
              width: '28px', 
              height: '28px', 
              borderRadius: '50%', 
              background: step >= 2 ? '#007bff' : '#e9ecef',
              color: step >= 2 ? 'white' : '#6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              {step > 2 ? <Check size={16} /> : '2'}
            </div>
            <span style={{ fontWeight: step === 2 ? 600 : 400 }}>Location</span>
          </div>
          
          <div style={{ width: '40px', height: '2px', background: step >= 3 ? '#007bff' : '#e9ecef' }} />
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            color: step >= 3 ? '#007bff' : '#6c757d'
          }}>
            <div style={{ 
              width: '28px', 
              height: '28px', 
              borderRadius: '50%', 
              background: step >= 3 ? '#007bff' : '#e9ecef',
              color: step >= 3 ? 'white' : '#6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              3
            </div>
            <span style={{ fontWeight: step === 3 ? 600 : 400 }}>Details</span>
          </div>
        </div>
      </div>

      {/* Step 1: Select Organization */}
      {step === 1 && (
        <div style={{ marginTop: '2rem' }}>
          <h2 className="section-title">Which organization is creating this collection?</h2>
          <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
            Select the organization that will own and manage this collection.
          </p>
          
          <div className="gallery-access-grid">
            {organizations.map((membership) => (
              <div
                key={membership.organization_id}
                onClick={() => handleOrgSelect(membership)}
                className="gallery-access-card org-card-link"
                style={{ 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  border: selectedOrg?.organization_id === membership.organizations?.organization_id 
                    ? '2px solid #007bff' 
                    : '1px solid #e9ecef'
                }}
              >
                <div className="org-logo-wrapper">
                  {membership.organizations?.logo ? (
                    <img 
                      src={addCloudinaryTransform(membership.organizations.logo, 't_fitmax-300/')}
                      alt={membership.organizations?.name || 'Organization'}
                      className="org-logo-img"
                    />
                  ) : (
                    <div className="org-logo-placeholder">
                      <Users size={32} color="#059669" />
                    </div>
                  )}
                </div>
                <div className="gallery-info">
                  <h3 className="gallery-name">{membership.organizations?.name || 'Unknown Organization'}</h3>
                  <span className="org-role-text">{membership.role}</span>
                  <p className="gallery-location">{membership.organizations?.description || 'No description'}</p>
                </div>
                <div style={{ marginLeft: 'auto', color: '#007bff' }}>
                  <ArrowRight size={20} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Select or Create Gallery */}
      {step === 2 && (
        <div style={{ marginTop: '1.5rem' }}>
          {/* Selected org indicator - More compact */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: '#f8f9fa',
            borderRadius: '6px',
            marginBottom: '1rem',
            border: '1px solid #e9ecef'
          }}>
            <div className="org-logo-wrapper" style={{ width: '24px', height: '24px' }}>
              {selectedOrg?.logo ? (
                <img 
                  src={addCloudinaryTransform(selectedOrg.logo, 't_fitmax-300/')}
                  alt={selectedOrg.name}
                  className="org-logo-img"
                />
              ) : (
                <div className="org-logo-placeholder" style={{ width: '24px', height: '24px' }}>
                  <Users size={14} color="#059669" />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#212529' }}>{selectedOrg?.name}</span>
              <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>• Creating for this organization</span>
            </div>
            <button 
              onClick={() => { setStep(1); setSelectedOrg(null); setSelectedGallery(null); }}
              style={{ 
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                fontSize: '0.8rem',
                padding: '0.25rem 0.5rem'
              }}
            >
              Change
            </button>
          </div>

          <h2 className="section-title" style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Where will this be hosted?</h2>
          <p style={{ color: '#6c757d', marginBottom: '1rem', fontSize: '0.9rem' }}>
            {selectedGallery 
              ? 'Selected gallery/venue for this collection.'
              : 'Search for an existing gallery/venue or create a new one.'
            }
          </p>

          {/* Show selected gallery OR search interface */}
          {selectedGallery ? (
            <>
              {/* Selected gallery display - Compact */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                background: '#f0f9ff',
                border: '1px solid #007bff',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '6px',
                  background: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 size={18} color="#007bff" />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>
                    {selectedGallery.name}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                     {selectedGallery.type && (
                        <span style={{ fontSize: '0.75rem', color: '#6c757d', background: '#e9ecef', padding: '0 4px', borderRadius: '4px' }}>
                        {selectedGallery.type}
                        </span>
                     )}
                     {selectedGallery.address && (
                        <p style={{ 
                        margin: 0, 
                        fontSize: '0.75rem', 
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                        }}>
                        <MapPin size={10} />
                        {selectedGallery.address}
                        </p>
                     )}
                  </div>
                </div>
                <button
                  onClick={handleClearSelection}
                  style={{
                    padding: '0.35rem 0.75rem',
                    background: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: '#495057'
                  }}
                >
                  Change
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Search box & Create Button - Compact */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search 
                        size={16} 
                        style={{ 
                        position: 'absolute', 
                        left: '10px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: '#6c757d'
                        }} 
                    />
                    <input
                        type="text"
                        placeholder="Search galleries..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        style={{
                        width: '100%',
                        padding: '0.6rem 0.6rem 0.6rem 36px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        fontSize: '0.9rem'
                        }}
                    />
                  </div>
                  
                  <button
                    onClick={handleCreateNewGallery}
                    style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.6rem 1rem',
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#495057',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap'
                    }}
                  >
                    <Plus size={16} />
                    <span>New Gallery</span>
                  </button>
              </div>

              {/* Gallery list */}
              {galleriesLoading ? (
            <div className="loading-state" style={{ padding: '1rem' }}>
              <div className="loading-spinner small"></div>
              <p style={{ fontSize: '0.9rem' }}>Searching...</p>
            </div>
          ) : !hasSearched ? (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6c757d' }}>
              <Search size={24} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p style={{ fontSize: '0.9rem' }}>Type to search</p>
            </div>
          ) : galleries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6c757d' }}>
              <MapPin size={24} style={{ marginBottom: '0.5rem' }} />
              <p style={{ fontSize: '0.9rem' }}>No galleries found</p>
            </div>
          ) : (
            <>
              {/* Sort options and results count */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.75rem',
                fontSize: '0.8rem',
                color: '#6c757d'
              }}>
                <span>{totalCount} found</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Sort:</span>
                  <select 
                    value={sortBy} 
                    onChange={(e) => handleSortChange(e.target.value)}
                    style={{
                      padding: '0.2rem 0.4rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      background: 'white'
                    }}
                  >
                    <option value="name">Name</option>
                    <option value="type">Type</option>
                    <option value="address">Loc</option>
                  </select>
                </div>
              </div>
              
              <div className="gallery-access-grid" style={{ gap: '0.75rem' }}>
                {galleries.map((gallery) => (
                  <div
                    key={gallery.gallery_id}
                    onClick={() => handleGallerySelect(gallery)}
                    className="gallery-access-card compact"
                    style={{ 
                      cursor: 'pointer',
                      border: selectedGallery?.gallery_id === gallery.gallery_id 
                        ? '1px solid #007bff' 
                        : '1px solid #e9ecef',
                      background: selectedGallery?.gallery_id === gallery.gallery_id
                        ? '#f0f9ff'
                        : '#ffffff',
                      padding: '0.75rem'
                    }}
                  >
                    <div className="gallery-icon" style={{ width: '32px', height: '32px' }}>
                      <Building2 size={16} color="#007bff" />
                    </div>
                    <div className="gallery-info">
                      <h3 className="gallery-name" style={{ fontSize: '0.9rem' }}>{gallery.name}</h3>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {gallery.type && (
                            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>{gallery.type}</span>
                        )}
                        <p style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.25rem',
                            fontSize: '0.75rem',
                            color: '#6c757d',
                            margin: 0
                        }}>
                            <MapPin size={10} />
                            {gallery.address || 'No Addr'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginTop: '1rem'
                }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.4rem 0.6rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      fontSize: '0.8rem'
                    }}
                  >
                    <ChevronLeft size={14} />
                    Prev
                  </button>
                  <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.4rem 0.6rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      fontSize: '0.8rem'
                    }}
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
            </>
          )}

          {/* Continue button */}
          {selectedGallery && (
            <div style={{ 
              marginTop: '1.5rem',
              padding: '0',
              background: 'transparent',
              boxShadow: 'none'
            }}>
              <button
                onClick={handleContinue}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Continue to Collection Details
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CuratorCreateCollectionWizard;
